/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ImportPlan, ImportScanEvent, OxleyExport, Product, ProductLine, Rule, SyncRun, ValueDriver, Version, WebsiteScanResult } from "@/domain/types";
import { callFn, db, streamFn } from "@/infra/cloud-adapter/client";

export async function getCatalog(workspaceId: string): Promise<{ lines: ProductLine[]; products: Product[]; drivers: ValueDriver[] }> {
  const [p, f, c, vd, vdf, pl] = await Promise.all([
    db().from("products").select("*").eq("workspace_id", workspaceId).order("name"),
    db().from("features").select("*").order("external_ref"),
    db().from("classifications").select("*").order("created_at", { ascending: false }),
    db().from("value_drivers").select("*").eq("workspace_id", workspaceId).order("name"),
    db().from("value_driver_features").select("*"),
    db().from("product_lines").select("*").order("name"),
  ]);
  for (const r of [p, f, c, vd, vdf, pl]) if (r.error) throw r.error;
  const drivers: ValueDriver[] = (vd.data ?? []).map((d: any) => ({ id: d.id, name: d.name, description: d.description, uniqueness: d.uniqueness ?? null, brand_promise: d.brand_promise ?? null, external_ref: d.external_ref ?? null, feature_ids: (vdf.data ?? []).filter((x: any) => x.value_driver_id === d.id).map((x: any) => x.feature_id) }));
  const latestCls = new Map<string, any>();
  for (const row of c.data ?? []) if (!latestCls.has(row.feature_id)) latestCls.set(row.feature_id, row);
  const products: Product[] = (p.data ?? []).map((prod: any) => ({
    id: prod.id, workspace_id: prod.workspace_id, product_line_id: prod.product_line_id ?? null, name: prod.name, description: prod.description, external_ref: prod.external_ref,
    features: (f.data ?? []).filter((x: any) => x.product_id === prod.id).map((x: any) => {
      const cl = latestCls.get(x.id);
      return {
        id: x.id, product_id: x.product_id, name: x.name, description: x.description, external_ref: x.external_ref,
        classification: cl ? { value_score: cl.value_score, adoption_score: cl.adoption_score, adoption_rating: cl.adoption_rating ?? null, value_rating: cl.value_rating ?? null, quadrant: cl.quadrant, source: cl.source, source_ref: cl.source_ref } : null,
        value_drivers: drivers.filter((d) => d.feature_ids.includes(x.id)).map((d) => d.name),
      };
    }),
  }));
  const lines: ProductLine[] = (pl.data ?? []).map((l: any) => ({ id: l.id, name: l.name, external_ref: l.external_ref }));
  return { lines, products, drivers };
}

export async function listVersions(workspaceId: string): Promise<Version[]> {
  const { data, error } = await db().from("versions").select("*").eq("workspace_id", workspaceId).order("published_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Version[];
}

export async function listRules(): Promise<Rule[]> {
  const { data, error } = await db().from("rules").select("code,name,description,severity").order("code");
  if (error) throw error;
  return (data ?? []) as Rule[];
}

export async function lastSync(tenantId: string): Promise<SyncRun | null> {
  const { data, error } = await db().from("catalog_sync_runs").select("*").eq("tenant_id", tenantId).order("created_at", { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return (data as SyncRun) ?? null;
}

/** A website read. With `product_ref` (a product already in the catalog) it becomes that product's next version; with `product_name` and
 * `product_line_name` it is a new product of that name and line. */
export type ImportSource = { source: "website"; file: unknown; product_ref?: string; product_name?: string; product_line_name?: string };
export type ImportPreview = { plan: ImportPlan; counts: Record<string, number>; sample: boolean };

export const oxleyStatus = (tenant_id: string) => callFn<{ connected: boolean }>("oxley-status", { tenant_id });
export const previewImport = (tenant_id: string, workspace_id: string, src: ImportSource) => callFn<ImportPreview>("catalog-import-plan", { tenant_id, workspace_id, ...src });
/** `pages` are the pages the catalog was read from; `sourcePage` is the address the person entered, kept so the product's next version is read from the same page. */
export const runImport = (tenant_id: string, workspace_id: string, src: ImportSource, pages: string[] = [], sourcePage?: string) =>
  callFn<{ draft_id: string; session_id: string; version_label: string | null }>("catalog-import-apply", { tenant_id, workspace_id, ...src, ...(pages.length ? { pages } : {}), ...(sourcePage ? { source_page: sourcePage } : {}) });

/** Adds a product by hand: an empty v1 draft (with its empty Good, Better and Best editions) and its Guided Packaging session. Only `draft_id` is used. */
export const addProduct = (tenant_id: string, workspace_id: string, name: string, product_line_name: string) =>
  callFn<{ draft_id: string; session_id: string; version_label: string }>("product-add", { tenant_id, workspace_id, name, product_line_name });

/** Changes a product's name and line in its open draft (the catalog changes when the draft is published). */
export const editProduct = (draft_id: string, name: string, product_line_name: string) =>
  callFn<{ committed: number }>("product-edit", { draft_id, name, product_line_name });

/** Renames a product line for this customer at once: its published products and open drafts, never another customer's. */
export const renameProductLine = (tenant_id: string, workspace_id: string, name: string, new_name: string) =>
  callFn<{ product_line_ref: string; products: number; drafts: number }>("product-line-rename", { tenant_id, workspace_id, name, new_name });

/** Reads a pricing page, reporting each stage as it happens. Resolves with the reading, or rejects with the failed stage named. */
export async function websiteScan(tenant_id: string, url: string, onEvent: (ev: ImportScanEvent) => void, signal?: AbortSignal): Promise<WebsiteScanResult> {
  const out: { result?: WebsiteScanResult; failure?: { stage: string; message: string } } = {};
  await streamFn("website-scan", { tenant_id, url }, (raw) => {
    const ev = raw as ImportScanEvent; // the API's NDJSON events (gw-oxley-be handlers.website_scan)
    onEvent(ev);
    if (ev.type === "done") out.result = ev;
    if (ev.type === "error") out.failure = ev;
  }, signal);
  if (out.failure) throw Object.assign(new Error(out.failure.message), { stage: out.failure.stage });
  if (!out.result) throw new Error("The website reading stopped before it finished. Try again.");
  return out.result;
}
